import React, { useRef, useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { debug } from '@/utils/debug';
import { useColdStartMutation } from '@/hooks/queries/useOptimizationQuery';
import { useCampaignOrganicMetrics } from '@/hooks/queries/useGSCQuery';
import { useFeedback } from '@/hooks/useFeedback';
import { ColdStartOptimization } from '@/components/features/ColdStartOptimization';
import { FeedbackCard } from '@/components/features/FeedbackCard';
import { SaveChangesBar } from '@/components/ui/SaveChangesBar';
import { LoadingSpinner } from '@/components/ui';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { computeCampaignRoas } from '@/utils/roas';
import { isMetaCampaign } from '@/types/campaign';

// Local hooks
import {
  useCampaignData,
  useCampaignState,
  useStickyHeader,
  useWizards,
} from './hooks';

// Local components
import {
  BackToCampaignsLink,
  CampaignHeader,
  CampaignSidebar,
  CampaignMetrics,
  CampaignStructure,
  CampaignLandingPages,
  CampaignManagement,
  CampaignWizards,
  CampaignDebug,
} from './components';

export function Campaign() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const campaignNameRef = useRef<HTMLHeadingElement>(null);

  // Dev mode state
  const [devMode, setDevMode] = useState(debug.isDev());

  // Listen for debug mode changes
  useEffect(() => {
    const updateMode = () => {
      const currentMode = debug.isDev();
      setDevMode(currentMode);
      console.log('Campaign: Mode updated to', currentMode ? 'DEVELOPMENT' : 'PRODUCTION');
    };
    const unsubscribe = debug.onModeChange(updateMode);
    updateMode();
    return unsubscribe;
  }, []);

  // Log component mount
  useEffect(() => {
    debug.log('Campaign: Component mounted for ID', id);
  }, [id]);

  // Custom hooks for data and state
  const campaignData = useCampaignData(id!);
  const campaignState = useCampaignState({
    campaignId: id!,
    campaign: campaignData.campaign,
    displayCampaign: campaignData.displayCampaign,
    landingPageData: campaignData.landingPageData,
    resetRemovedEntities: campaignData.resetRemovedEntities,
  });
  const wizards = useWizards();
  // Pass !isLoading so the observer sets up only after CampaignHeader is rendered
  const showStickyName = useStickyHeader(campaignNameRef, !campaignData.isLoading);
  const feedback = useFeedback({ pageContext: 'campaign', campaignId: id });
  const coldStartMutation = useColdStartMutation();
  // GSC organic metrics are Google-Search-Console-backed and keyed on a
  // Google Ads campaign id. For Meta campaigns the lookup is meaningless
  // (and the GSC id would be a Meta numeric campaign id that GSC can't
  // resolve). Detect Meta from the route path (synchronous) so the
  // query is disabled from first render — using `displayCampaign`
  // would leak one stray GSC call before the data resolves. The
  // unified-page route is `/meta-campaign/:id`; the legacy
  // `/campaign/:id` and the new `/google-campaign/:id` are both
  // Google paths so `isMeta` stays false for them.
  const location = useLocation();
  const isMetaRoute = location.pathname.startsWith('/meta-campaign/');
  // Keep the data-driven check too — it covers the unlikely case where a
  // Meta campaign id is loaded under a non-Meta route (debug/dev paths).
  const isMeta = isMetaRoute ||
    (!!campaignData.displayCampaign && isMetaCampaign(campaignData.displayCampaign));
  const {
    data: organicMetrics,
    isLoading: isOrganicMetricsLoading,
  } = useCampaignOrganicMetrics(isMeta ? '' : id!);

  // ROAS — consolidated under the shared `computeCampaignRoas` utility so
  // this page can't drift from the Account-page cards. Previously this
  // had its own hand-rolled two-tier branch with a different policy
  // (per-campaign `hasConversionsValue` instead of account-level) which
  // produced subtly different numbers on the same campaign across views.
  const hasConversionTracking = campaignData.appConfig?.configuration?.has_conversion_tracking === true;
  const revenuePerClick = campaignData.appConfig?.configuration?.revenue_per_click as number | undefined;
  let roas: number | null = null;
  if (campaignData.displayCampaign) {
    const { displayCampaign } = campaignData;
    const hasConversionsValueForThisCampaign =
      displayCampaign.conversions_value != null && displayCampaign.conversions_value > 0;
    const result = computeCampaignRoas({
      cost: displayCampaign.cost,
      clicks: displayCampaign.clicks,
      conversionsValue: displayCampaign.conversions_value,
      revenuePerClick,
      // Detail page predates the account-level toggle plumbing; until
      // the saved-config carries `use_conversion_based_roas` as a true
      // top-level setting, mirror the legacy behavior: prefer
      // conversion-based when this campaign itself has conversion value.
      // TODO: thread the account-level toggle through `appConfig`.
      useConversionBasedRoas: hasConversionsValueForThisCampaign,
      conversionTrackingAvailable: hasConversionsValueForThisCampaign,
    });
    roas = result.roas;
  }

  // Loading state
  if (campaignData.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-4" label="Loading campaign" />
          <p className="text-gray-600">Loading campaign details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (campaignData.error) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Campaign</h2>
        <p className="text-gray-600 mb-4">
          {campaignData.error instanceof Error ? campaignData.error.message : 'An unexpected error occurred'}
        </p>
        <BackToCampaignsLink />
      </div>
    );
  }

  // Not found state
  if (!campaignData.campaign && !campaignData.isLoading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Campaign Not Found</h2>
        <p className="text-gray-600 mb-4">
          The campaign with ID "{id}" could not be found.
        </p>
        <BackToCampaignsLink />
      </div>
    );
  }

  // Null guard
  if (!campaignData.displayCampaign) {
    return null;
  }

  const { displayCampaign, hasFullStructure, structureSummary, landingPageData, rdaCache, isLoadingRDAs, demandGenCache, isLoadingDemandGenAds } = campaignData;
  const { selectedAdGroups, selectedLandingPages, handleAdGroupToggle, handleLandingPageToggle, updateOriginalBidsAfterSave } = campaignState;

  return (
    <ErrorBoundary>
      <div>
        <CampaignHeader
          campaignName={displayCampaign.campaign_name}
          campaignType={displayCampaign.campaign_type}
          campaignNameRef={campaignNameRef}
        />

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Campaign Details + Feedback Card */}
          <CampaignSidebar
            campaign={displayCampaign}
            showStickyName={showStickyName}
            roas={roas}
            feedback={feedback}
          />

          {/* Main Content */}
          <div className="flex-1 min-w-0 space-y-6">
            <CampaignMetrics
              campaign={displayCampaign}
              hasConversionTracking={hasConversionTracking}
              isLoading={campaignData.isLoading || isOrganicMetricsLoading}
              organicMetrics={organicMetrics}
            />

            {/* Campaign Structure Summary */}
            {structureSummary && (
              <CampaignStructure
                campaign={displayCampaign}
                campaignId={id!}
                structureSummary={structureSummary}
                rdaCache={rdaCache}
                isLoadingRDAs={isLoadingRDAs}
                demandGenCache={demandGenCache}
                isLoadingDemandGenAds={isLoadingDemandGenAds}
                onEntityRemoved={campaignData.handleEntityRemoved}
                wizards={wizards}
              />
            )}

            {/* Flouri Optimize Bids Section — Google-only per
                META_INTEGRATION_PLAN.md §10.2 (bid optimization stays
                Google-only). Meta's bidding is mostly automated
                (Lowest Cost / Bid Cap / Cost Cap) so the cold-start
                wizard doesn't apply. */}
            {hasFullStructure && !isMeta && (
              <ColdStartOptimization
                campaign={displayCampaign}
                selectedAdGroups={selectedAdGroups}
                onAdGroupToggle={handleAdGroupToggle}
                onOptimize={async (request) => {
                  const result = await coldStartMutation.mutateAsync(request);
                  return result;
                }}
                isLoading={coldStartMutation.isPending}
                result={coldStartMutation.data || null}
                error={coldStartMutation.error as Error | null}
              />
            )}

            {/* Landing Pages */}
            {hasFullStructure && (
              <CampaignLandingPages
                campaign={displayCampaign}
                landingPageData={landingPageData}
                selectedLandingPages={selectedLandingPages}
                selectedAdGroups={selectedAdGroups}
                onLandingPageToggle={handleLandingPageToggle}
              />
            )}

            {/* Campaign Management Tabs */}
            <CampaignManagement
              campaign={displayCampaign}
              hasFullStructure={hasFullStructure}
              selectedLandingPages={selectedLandingPages}
              rdaCache={rdaCache}
              isLoadingRDAs={isLoadingRDAs}
              demandGenCache={demandGenCache}
              isLoadingDemandGenAds={isLoadingDemandGenAds}
              onCreateDemandGenAd={wizards.demandGenAdWizard.open}
            />
          </div>
        </div>

        {/* Debug Panel */}
        <CampaignDebug
          devMode={devMode}
          campaign={campaignData.campaign}
          displayCampaign={displayCampaign}
          hasFullStructure={hasFullStructure}
          isLoading={campaignData.isLoading}
        />

        {/* Creation Wizards */}
        <CampaignWizards
          campaign={displayCampaign}
          campaignId={id!}
          wizards={wizards}
        />

        {/* Feedback Card - Mobile fallback */}
        {feedback.isVisible && feedback.currentQuestion && (
          <div className="lg:hidden fixed bottom-4 right-4 z-40 w-auto max-w-[min(14rem,calc(100vw-2rem))]">
            <FeedbackCard
              question={feedback.currentQuestion}
              onSubmit={feedback.submitResponse}
              isSubmitting={feedback.isSubmitting}
              error={feedback.error}
            />
          </div>
        )}

        {/* Save Changes Bar */}
        <SaveChangesBar
          onSaveSuccess={async () => {
            toast.success('All changes saved successfully!');
            // Update original bids optimization to reflect the new saved state
            updateOriginalBidsAfterSave();
            // Refresh campaign data after save
            try {
              await queryClient.invalidateQueries({ queryKey: ['app-config'] });
              await queryClient.invalidateQueries({ queryKey: ['campaign-from-cache', id] });
            } catch (error) {
              debug.error('Failed to invalidate queries after saving campaign changes', error);
              toast.error('Changes were saved, but we could not refresh the latest campaign data. Please try reloading the page.');
            }
          }}
          onSaveError={(errors) => {
            errors.forEach((error) => toast.error(error));
          }}
        />
      </div>
    </ErrorBoundary>
  );
}
